import {
  Box,
} from '@mui/material';
import Places from '../Places/Places';
import ImageCarousel from '../../Components/ ImageCarousel';
import LocationComponent from '../../Components/Map/LocationComponent';
import { RootState } from '../../store/rootReducers';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import DashboardPlaceScreen from './DashboardPlaceScreen';
import { setDashboard } from '../../store/slices/DashboardSlice';
import DashboardTripScreen from './DashboardTripScreen';
import DashboardTopBar from './DashboardTopBar';
import UserSearchFab from '../../Components/Fab/UserSearchFab';


const INTRO_REDIRECTED_SESSION_KEY = 'introRedirectedThisSession';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const banners = useSelector((state: RootState) => state.dashboard.introSliders);
  const location = useSelector((state: RootState) => state.location.location);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  
  // Memoize location coordinates to prevent unnecessary API calls
  const locationLat = useMemo(() => location?.lat, [location?.lat]);
  const locationLng = useMemo(() => location?.lng, [location?.lng]);
  
  // Track the last API call coordinates to prevent duplicate calls
  const lastApiCallRef = useRef<{ lat: number; lng: number } | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const fallbackFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Check if Notification API is available
    if (typeof Notification === 'undefined') {
      return;
    }
    
    if (Notification.permission !== 'granted' && !(window as any).__notificationMsgShown) {
      (window as any).__notificationMsgShown = true;
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: 'Enable notifications from browser settings to never miss updates',
            type: 'warning',
            onClick: async () => {
              if (typeof Notification === 'undefined') return;
              const result = await Notification.requestPermission();
              if (result === 'granted') {
                window.dispatchEvent(
                  new CustomEvent('app-toast', {
                    detail: { message: 'Notifications enabled!', type: 'success' },
                  }),
                );
              } 
            },
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    const runFetch = async (endpoint: string) => {
      // Skip if already fetching
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const res = await request(endpoint, {
          method: 'GET',
          authorization: isLoggedIn,
        }) as any;
        dispatch(setDashboard({
          banners: res.data.banners,
          introSliders: res.data.introSliders,
          categories: res.data.postCategories,
          newPlaces: res.data.newPlaces,
          curatedPlaces: res.data.curatedPlaces,
          tripsItinerary: res.data.tripsItinerary,
        }));
      } catch (error) {
        console.error('Error fetching landing data:', error);
      } finally {
        isFetchingRef.current = false;
      }
    };

    const clearFallbackTimer = () => {
      if (fallbackFetchTimerRef.current) {
        clearTimeout(fallbackFetchTimerRef.current);
        fallbackFetchTimerRef.current = null;
      }
    };

    const fetchLanding = async () => {
      let endpoint = '/landing';

      // Add location parameters when available
      if (locationLat && locationLng) {
        clearFallbackTimer();

        // Check if coordinates have meaningfully changed (avoid calls for tiny coordinate changes)
        const lastCall = lastApiCallRef.current;
        const hasChanged = !lastCall || 
          Math.abs(lastCall.lat - locationLat) > 0.0001 || 
          Math.abs(lastCall.lng - locationLng) > 0.0001;
        
        if (hasChanged) {
          const queryParams = new URLSearchParams({
            latitude: locationLat.toString(),
            longitude: locationLng.toString(),
          });
          endpoint = `${endpoint}?${queryParams.toString()}`;
          lastApiCallRef.current = { lat: locationLat, lng: locationLng };
          await runFetch(endpoint);
        }
      } else {
        // Delay fallback fetch so location has time to resolve; avoids duplicate no-location + location calls
        if (!lastApiCallRef.current && !fallbackFetchTimerRef.current) {
          fallbackFetchTimerRef.current = setTimeout(() => {
            fallbackFetchTimerRef.current = null;
            // If location is still missing and we still haven't fetched anything, call once without params
            if (!lastApiCallRef.current && !locationLat && !locationLng) {
              lastApiCallRef.current = { lat: 0, lng: 0 };
              runFetch('/landing');
            }
          }, 1200);
        }
      }
    };
    
    fetchLanding();

    return () => {
      clearFallbackTimer();
    };
  }, [locationLat, locationLng, dispatch, isLoggedIn]);

  // Check intro slider condition and navigate if needed
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenIntroSlider');
    const introRedirectedThisSession = sessionStorage.getItem(INTRO_REDIRECTED_SESSION_KEY);
    if (!hasSeenIntro && !introRedirectedThisSession && banners && banners.length > 0) {
      // Guard against late async banner responses redirecting users repeatedly mid-session
      sessionStorage.setItem(INTRO_REDIRECTED_SESSION_KEY, 'true');
      navigate('/intro');
    }
  }, [banners, navigate]);

  return (
    <>
      <DashboardTopBar />
          <ImageCarousel/>
      <Box p={2} sx={{ mb:6 }}>
          <DashboardPlaceScreen title="Nearby places" />
          <DashboardTripScreen  />
          <DashboardPlaceScreen title="Newly added places" noCategories />
        {/* <Places/> */}
      </Box>
      <LocationComponent/>
      <UserSearchFab />
    </>
  );
};

export default Dashboard;
